import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, Copy, History, Link2Off, MapPin, MoreHorizontal, RotateCcw, UserRound, X } from 'lucide-react';
import { deviceDisplayName, type Device, type DeviceAssignmentType } from '@mytag/shared';
import { useUpdateDeviceMetadata } from '@/hooks/queries';
import { ErrorState } from '@/components/common/ErrorState';
import { Button, Field, Input, Select } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';

export type DeviceMetadataDialogKind = 'rename' | 'notes';

export function DeviceMetadataDialog({
  device,
  kind,
  onClose,
  onSaved,
}: {
  device: Device;
  kind: DeviceMetadataDialogKind;
  onClose: () => void;
  onSaved: (message: string) => void;
}): JSX.Element {
  const update = useUpdateDeviceMetadata();
  const metadata = device.localMetadata;
  const [localName, setLocalName] = useState(metadata?.localName ?? deviceDisplayName(device));
  const [notes, setNotes] = useState(metadata?.notes ?? '');
  const [assignmentType, setAssignmentType] = useState<DeviceAssignmentType>(metadata?.assignmentType ?? 'none');
  const [assignmentLabel, setAssignmentLabel] = useState(metadata?.assignmentLabel ?? '');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !update.isPending) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, update.isPending]);

  const saveRename = (value: string | null) => {
    update.mutate(
      { deviceNum: device.deviceNum, update: { localName: value } },
      {
        onSuccess: () => {
          onSaved(value === null ? 'Nom fournisseur restauré.' : 'Nom local enregistré.');
          onClose();
        },
      },
    );
  };

  const saveNotes = () => {
    update.mutate(
      {
        deviceNum: device.deviceNum,
        update: {
          notes,
          assignmentType,
          assignmentLabel: assignmentType === 'none' ? null : assignmentLabel,
        },
      },
      {
        onSuccess: () => {
          onSaved('Notes et assignation enregistrées.');
          onClose();
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/40 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !update.isPending) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-metadata-title"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-xl"
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 id="device-metadata-title" className="text-base font-semibold">
              {kind === 'rename' ? 'Renommer dans MyTag' : 'Notes et assignation'}
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Nom actuel : {deviceDisplayName(device)}
            </p>
          </div>
          <Button type="button" size="icon" variant="ghost" aria-label="Fermer" icon={<X className="h-4 w-4" />} onClick={onClose} />
        </div>

        {kind === 'rename' ? (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = localName.trim();
              if (trimmed) saveRename(trimmed);
            }}
          >
            <Field label="Nom local" htmlFor="local-device-name" hint="Visible uniquement dans MyTag, de 1 à 60 caractères.">
              <Input
                id="local-device-name"
                value={localName}
                minLength={1}
                maxLength={60}
                required
                autoFocus
                onChange={(event) => setLocalName(event.target.value)}
              />
            </Field>
            {update.isError ? <ErrorState error={update.error} title="Enregistrement impossible" /> : null}
            <div className="flex flex-wrap justify-end gap-2">
              {metadata?.localName ? (
                <Button
                  type="button"
                  variant="outline"
                  loading={update.isPending}
                  icon={<RotateCcw className="h-4 w-4" />}
                  onClick={() => saveRename(null)}
                >
                  Restaurer le nom fournisseur
                </Button>
              ) : null}
              <Button type="button" variant="ghost" disabled={update.isPending} onClick={onClose}>Annuler</Button>
              <Button type="submit" loading={update.isPending} disabled={!localName.trim()}>Enregistrer</Button>
            </div>
          </form>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              saveNotes();
            }}
          >
            <Field label="Notes" htmlFor="device-notes" hint={`${notes.length}/2 000 caractères`}>
              <textarea
                id="device-notes"
                value={notes}
                maxLength={2_000}
                rows={7}
                className="focus-ring w-full resize-y rounded-md border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground"
                onChange={(event) => setNotes(event.target.value)}
              />
            </Field>
            <Field label="Type d’assignation" htmlFor="assignment-type">
              <Select
                id="assignment-type"
                value={assignmentType}
                onChange={(event) => {
                  const next = event.target.value as DeviceAssignmentType;
                  setAssignmentType(next);
                  if (next === 'none') setAssignmentLabel('');
                }}
              >
                <option value="none">Non assigné</option>
                <option value="person">Personne</option>
                <option value="vehicle">Véhicule</option>
              </Select>
            </Field>
            {assignmentType !== 'none' ? (
              <Field
                label={assignmentType === 'person' ? 'Nom de la personne' : 'Identification du véhicule'}
                htmlFor="assignment-label"
              >
                <Input
                  id="assignment-label"
                  value={assignmentLabel}
                  maxLength={120}
                  required
                  onChange={(event) => setAssignmentLabel(event.target.value)}
                />
              </Field>
            ) : null}
            {update.isError ? <ErrorState error={update.error} title="Enregistrement impossible" /> : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" disabled={update.isPending} onClick={onClose}>Annuler</Button>
              <Button
                type="submit"
                loading={update.isPending}
                disabled={assignmentType !== 'none' && !assignmentLabel.trim()}
              >
                Enregistrer
              </Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

export function DeviceActionsMenu({
  device,
  onRename,
  onArchiveToggle,
  onUnbind,
  unbindDisabled = false,
  onMessage,
}: {
  device: Device;
  onRename: () => void;
  onArchiveToggle: () => void;
  /** Dissociation réelle chez Vernal ; distincte de l'archivage local. */
  onUnbind: () => void;
  unbindDisabled?: boolean;
  onMessage: (message: string, error?: boolean) => void;
}): JSX.Element {
  const menu = useRef<HTMLDetailsElement>(null);
  const close = () => { if (menu.current) menu.current.open = false; };
  const run = (action: () => void) => { close(); action(); };
  const copy = async (label: string, value: string) => {
    close();
    try {
      await navigator.clipboard.writeText(value);
      onMessage(`${label} copié.`);
    } catch {
      onMessage(`Impossible de copier ${label.toLowerCase()}.`, true);
    }
  };
  const archived = Boolean(device.localMetadata?.archivedAt);

  return (
    <details ref={menu} className="relative">
      <summary
        className="focus-ring flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground [&::-webkit-details-marker]:hidden"
        aria-label={`Plus d’actions pour ${deviceDisplayName(device)}`}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </summary>
      <div className="absolute right-0 z-20 mt-1 w-64 rounded-md border border-border bg-card p-1 shadow-xl">
        <MenuLink to={`/devices/${device.deviceNum}#map`} icon={<MapPin className="h-4 w-4" />} onClick={close}>Voir sur la carte</MenuLink>
        <MenuLink to={`/devices/${device.deviceNum}/history`} icon={<History className="h-4 w-4" />} onClick={close}>Historique</MenuLink>
        <MenuButton icon={<UserRound className="h-4 w-4" />} onClick={() => run(onRename)}>Renommer dans MyTag</MenuButton>
        <MenuButton icon={archived ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />} onClick={() => run(onArchiveToggle)}>
          {archived ? 'Restaurer dans MyTag' : 'Archiver dans MyTag'}
        </MenuButton>
        <div className="my-1 border-t border-border" />
        <MenuButton icon={<Copy className="h-4 w-4" />} onClick={() => void copy('deviceNum', device.deviceNum)}>Copier le deviceNum</MenuButton>
        {device.mac ? <MenuButton icon={<Copy className="h-4 w-4" />} onClick={() => void copy('MAC', device.mac as string)}>Copier la MAC</MenuButton> : null}
        <div className="my-1 border-t border-border" />
        <MenuButton
          icon={<Link2Off className="h-4 w-4" />}
          disabled={unbindDisabled}
          tone="danger"
          onClick={() => run(onUnbind)}
        >
          Dissocier du compte Vernal
        </MenuButton>
      </div>
    </details>
  );
}

function menuClass(danger = false): string {
  return cn(
    'focus-ring flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs hover:bg-muted disabled:pointer-events-none disabled:opacity-50',
    danger ? 'text-danger hover:text-danger' : 'text-foreground',
  );
}

function MenuLink({ to, icon, onClick, children }: { to: string; icon: React.ReactNode; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return <Link to={to} className={menuClass()} onClick={onClick}>{icon}{children}</Link>;
}

function MenuButton({
  icon,
  onClick,
  children,
  disabled = false,
  tone,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  tone?: 'danger';
}): JSX.Element {
  return (
    <button type="button" className={menuClass(tone === 'danger')} disabled={disabled} onClick={onClick}>
      {icon}
      {children}
    </button>
  );
}
